// TODO: change it to axios
import { useAuthStore } from '@/stores/auth';
import { useRecaptcha } from '@/composables/useRecaptcha';
import { FirebaseAuthService } from '@/firebase/firebase-auth-service';

export const fetchWrapper = {
  get: request('GET'),
  post: request('POST'),
  put: request('PUT'),
  delete: request('DELETE'),
  patch: request('PATCH'),
};

const HTTP_STATUS_UNAUTHORIZED = 401;
const HTTP_STATUS_TOO_MANY_REQUESTS = 429;
const NETWORK_ERROR_CODE = 'network_error';
const GENERIC_ERROR_CODE = 'generic_error';
const TOO_MANY_REQUESTS_ERROR_CODE = 'too_many_requests';
const THROTTLER_EXCEPTION_MESSAGE = 'ThrottlerException: Too Many Requests';
let refreshTokenPromise: Promise<void> | null = null;

interface RequestOptions {
  method: string;
  headers: Record<string, string>;
  body?: string;
}

const recaptchaEndpoints = new Map([
  [`${import.meta.env.VITE_API_URL}/v3/auth/login`, 'login'],
  [`${import.meta.env.VITE_API_URL}/v3/auth/forgot-password`, 'password_reset'],
  [`${import.meta.env.VITE_API_URL}/v3/auth/change-password`, 'password_change'],
  [`${import.meta.env.VITE_API_URL}/v3/auth/refresh-token`, 'token_refresh'],
  [`${import.meta.env.VITE_API_URL}/v3/company-registration`, 'company_registration'],
]);

function request(method: string) {
  return async (url: string, body?: object) => {
    if (!navigator.onLine) {
      const error = {
        code: NETWORK_ERROR_CODE,
        data: { message: 'No internet connection' },
        isNetworkError: true,
      };
      return Promise.reject(error);
    }
    const requestOptions = await createRequestOptions(method, url, body);
    return fetch(url, requestOptions).then((response) => handleResponse(response, url, method, body));
  };
}

// helper functions

function authHeader(url: string): Record<string, string> {
  // return auth header with jwt if user is logged in and request is to the api url
  const { jwt } = useAuthStore();
  const isLoggedIn = jwt?.accessToken;
  const isApiUrl = url.startsWith(import.meta.env.VITE_API_URL);
  if (isLoggedIn && isApiUrl) {
    return { Authorization: `Bearer ${jwt.accessToken}` };
  } else {
    return {};
  }
}

async function handleResponse(response: Response, url: string, method: string, body?: object): Promise<any> {
  const authStore = useAuthStore();
  const text = await response.text();
  const data = text ? JSON.parse(text) : null;
  if (!response.ok) {
    // Handle unauthorized (401) responses by attempting to refresh the token
    if (response.status === HTTP_STATUS_UNAUTHORIZED && authStore.jwt?.refreshToken) {
      try {
        await refreshToken();
        const newRequestOptions = await createRequestOptions(method, url, body);
        const retryResponse = await fetch(url, newRequestOptions);
        const retryText = await retryResponse.text();
        const retryData = retryText ? JSON.parse(retryText) : null;
        if (retryResponse.ok) {
          return retryData;
        } else {
          const error = {
            code: resolveErrorCode(retryResponse.status, retryData),
            data: retryData,
          };
          authStore.logout();
          return Promise.reject(error);
        }
      } catch (refreshError) {
        authStore.logout();
        return Promise.reject(refreshError);
      }
    }
    const error = {
      code: resolveErrorCode(response.status, data),
      data: data,
    };
    return Promise.reject(error);
  }

  return data;
}

function resolveErrorCode(status: number, data: any): string {
  if (data?.code) {
    return data.code;
  }

  if (status === HTTP_STATUS_TOO_MANY_REQUESTS || data?.statusCode === HTTP_STATUS_TOO_MANY_REQUESTS) {
    return TOO_MANY_REQUESTS_ERROR_CODE;
  }

  if (data?.message === THROTTLER_EXCEPTION_MESSAGE) {
    return TOO_MANY_REQUESTS_ERROR_CODE;
  }

  return GENERIC_ERROR_CODE;
}

export async function refreshToken(): Promise<void> {
  if (refreshTokenPromise) {
    return refreshTokenPromise;
  }

  refreshTokenPromise = performTokenRefresh();

  try {
    await refreshTokenPromise;
  } finally {
    refreshTokenPromise = null;
  }
}

async function performTokenRefresh(): Promise<void> {
  const authStore = useAuthStore();
  const { jwt, updateTokens } = authStore;

  if (!jwt?.refreshToken) {
    throw new Error('No refresh token available');
  }
  const requestOptions: RequestOptions = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      refreshToken: jwt.refreshToken,
    }),
  };
  const refreshUrl = `${import.meta.env.VITE_API_URL}/v3/auth/refresh-token`;

  const response = await fetch(refreshUrl, requestOptions);
  const text = await response.text();
  const refreshedJwt = text ? JSON.parse(text) : null;

  if (!response.ok) {
    throw new Error(refreshedJwt?.message || 'Token refresh failed');
  }

  updateTokens(refreshedJwt);

  try {
    const firebaseToken = await fetchWrapper.post(`${import.meta.env.VITE_API_URL}/v3/auth/firebase-token`, {});
    if (firebaseToken?.token) {
      await FirebaseAuthService.signInWithToken(firebaseToken.token);
    } else {
      console.warn('No Firebase token received');
    }
  } catch (firebaseError) {
    console.warn('Firebase token refresh failed:', firebaseError);
  }
}

async function createRequestOptions(method: string, url: string, body?: object): Promise<RequestOptions> {
  const requestOptions: RequestOptions = {
    method,
    headers: authHeader(url),
  };

  if (body) {
    requestOptions.headers['Content-Type'] = 'application/json';
    requestOptions.body = JSON.stringify(body);
  }
  if (recaptchaEndpoints.has(url)) {
    const { executeRecaptcha } = useRecaptcha();
    const action = recaptchaEndpoints.get(url) as string;
    const token = await executeRecaptcha(action);

    requestOptions.headers['X-Recaptcha-Token'] = token;
  }

  return requestOptions;
}
